import React, { useState, useEffect } from "react";
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import communityService from "../services/communityService.js";

const StoryModalView = ({ show, onClose, story, onSave, currentUser }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({
    title: '',
    name: '',
    content: ''
  });

  useEffect(() => {
    if (story) {
      setEditData({
        title: story.title || '',
        name: story.name || '',
        content: story.content || ''
      });
    }
    // Reset edit mode when story changes
    setIsEditing(false);
  }, [story]);

  if (!show || !story) return null;

  const canEdit = communityService.canEdit(story, currentUser);

  const handleSave = async () => {
    if (onSave) {
      const success = await onSave(story.id, editData);
      if (success) {
        setIsEditing(false);
        // Update the story object with new data
        Object.assign(story, editData);
      }
    }
  };

  const handleCancel = () => {
    setEditData({
      title: story.title || '',
      name: story.name || '',
      content: story.content || ''
    });
    setIsEditing(false);
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 py-8">
        <div className="bg-white rounded-lg shadow-2xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto border-2 border-gray-300">
          {isEditing ? (
            // Edit Mode
            <>
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Edit Story</h2>
                <button
                  onClick={handleCancel}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Story Title
                  </label>
                  <input
                    type="text"
                    value={editData.title}
                    onChange={(e) => setEditData({ ...editData, title: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Enter story title..."
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Author Name
                  </label>
                  <input
                    type="text"
                    value={editData.name}
                    onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Enter author name..."
                  />
                </div>

                {story.imageUrl && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Featured Image
                    </label>
                    <img
                      src={story.imageUrl}
                      alt={story.title}
                      className="rounded w-full object-cover max-h-48"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Image editing not yet supported. Contact admin to change images.
                    </p>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Story Content
                  </label>
                  <ReactQuill
                    theme="snow"
                    value={editData.content}
                    onChange={(value) => setEditData({ ...editData, content: value })}
                    className="bg-white"
                    modules={{
                      toolbar: [
                        [{ 'header': [1, 2, 3, false] }],
                        ['bold', 'italic', 'underline'],
                        [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                        ['link'],
                        ['clean']
                      ]
                    }}
                    placeholder="Tell your story..."
                  />
                </div>
              </div>

              <div className="mt-6 flex justify-between">
                <button
                  onClick={handleCancel}
                  className="inline-flex items-center px-4 py-2 bg-gray-300 text-gray-700 text-sm font-medium rounded hover:bg-gray-400"
                >
                  Cancel
                </button>
                <div className="space-x-2">
                  <button
                    onClick={handleSave}
                    className="inline-flex items-center px-4 py-2 bg-green-600 text-white text-sm font-medium rounded hover:bg-green-700"
                  >
                    Save Changes
                  </button>
                </div>
              </div>
            </>
          ) : (
            // View Mode
            <>
              <div className="flex justify-between items-start mb-4">
                <h2 className="text-2xl font-bold text-gray-900">{story.title}</h2>
                <div className="flex items-center space-x-2">
                  {canEdit && (
                    <button
                      onClick={() => setIsEditing(true)}
                      className="inline-flex items-center px-3 py-1 bg-blue-100 text-blue-700 text-sm font-medium rounded hover:bg-blue-200"
                    >
                      <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                      Edit
                    </button>
                  )}
                  <button
                    onClick={onClose}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    ✕
                  </button>
                </div>
              </div>
              
              <p className="text-sm text-gray-500 mb-4">
                By {story.name} • {communityService.formatDate(story.createdAt)}
              </p>
              
              {story.imageUrl && (
                <img
                  src={story.imageUrl}
                  alt={story.title}
                  className="mb-4 rounded w-full object-cover"
                />
              )}
              
              <div 
                className="text-gray-700 leading-relaxed prose prose-sm max-w-none"
                dangerouslySetInnerHTML={{ __html: story.content }}
              />
              
              <div className="mt-6 text-right">
                <button
                  onClick={onClose}
                  className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded hover:bg-indigo-700"
                >
                  Close
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default StoryModalView;
